import { jsx, jsxs } from "react/jsx-runtime";
import find from "lodash.find";
import findIndex from "lodash.findindex";
import { useRef, useState, useContext, useEffect } from "react";
import Menu from "../../../common/assets/icons/Menu.js";
import { named } from "../../../utils/named.js";
import { ThemeContext } from "styled-components";
import { v4 } from "/app/node_modules/.pnpm/uuid@9.0.0/node_modules/uuid/dist/esm-browser/index.js";
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from "@dnd-kit/core";
import { sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Container, ListWrapper, Draggable, Item, Icon, ListInput, RemoveItem } from "./Styles.js";
import { getFormattedValue } from "./Renderer.js";
import { getCellValueIsEmpty } from "../../../utils/cells.js";
import { Icon as Icon$1 } from "@flatfile/design-system";
const ListItem = ({
  item,
  first,
  last,
  updateValue,
  insert,
  remove,
  next,
  focusKey,
  setFocusKey,
  prev
}) => {
  const themeContext = useContext(ThemeContext);
  const { error, key, warning } = item;
  const inputRef = useRef(null);
  const handleChange = (event) => {
    const { value } = event.currentTarget;
    updateValue({ value, key });
  };
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.key });
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && event.shiftKey) {
      event.stopPropagation();
      return insert(key);
    }
    if (event.key === "Backspace") {
      if (item.value === "") {
        event.preventDefault();
        return remove(key);
      }
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (last)
        return insert(key);
      return next(key);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      return prev(key);
    }
    if (event.key === "Tab") {
      event.preventDefault();
      if (!last && !(event.shiftKey && first)) {
        event.stopPropagation();
        if (event.shiftKey) {
          return prev(key);
        }
        return next(key);
      }
    }
  };
  const handleRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    return remove(key);
  };
  useEffect(() => {
    if (focusKey === key && (inputRef == null ? void 0 : inputRef.current)) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
    }
  }, [focusKey]);
  return /* @__PURE__ */ jsx(
    Draggable,
    {
      ref: setNodeRef,
      transform: CSS.Transform.toString(transform),
      transition,
      isDragging,
      children: /* @__PURE__ */ jsxs(
        Item,
        {
          role: "listitem",
          last,
          first,
          error,
          warning,
          children: [
            /* @__PURE__ */ jsx(Icon, { ...attributes, ...listeners, children: /* @__PURE__ */ jsx(Menu, { height: 10, width: 10, color: themeContext.cell.color }) }),
            /* @__PURE__ */ jsx(
              ListInput,
              {
                onFocus: () => setFocusKey(key),
                ref: inputRef,
                onKeyDown: handleKeyDown,
                value: item.value,
                onChange: handleChange
              }
            ),
            !getCellValueIsEmpty(item.value) && /* @__PURE__ */ jsx(RemoveItem, { onClickCapture: handleRemove, children: /* @__PURE__ */ jsx(Icon$1, { name: "cross" }) })
          ]
        }
      )
    }
  );
};
const Editor = (props) => {
  var _a, _b;
  const { data, keyPressValue, setCurrentValue, isNewRowCell } = props;
  const value = data.value;
  const errors = (_a = data.status) == null ? void 0 : _a.error;
  const warnings = (_b = data.status) == null ? void 0 : _b.warning;
  const emptyItem = { label: "", value: "", key: v4() };
  const hydrateEmptyList = [emptyItem];
  const formattedData = getFormattedValue(value, data.status, false);
  const getInitValues = () => {
    if (keyPressValue) {
      return [
        ...formattedData,
        { label: keyPressValue, value: keyPressValue, key: v4() }
      ];
    }
    if (formattedData == null ? void 0 : formattedData.length) {
      return [...formattedData, emptyItem];
    }
    return hydrateEmptyList;
  };
  const initWithKeys = getInitValues();
  const initOrder = initWithKeys.map(
    (item) => item.key
  );
  const orderRef = useRef(initOrder);
  const [list, setList] = useState(initWithKeys);
  const [focusKey, setFocusKey] = useState(initOrder[initOrder.length - 1]);
  const [first, setFirst] = useState(null);
  const [last, setLast] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const removeEmptyItems = (items) => {
    return items.filter((item) => item.value !== "");
  };
  const updateCurrentValue = (items) => {
    const updatedItems = removeEmptyItems(items);
    if (setCurrentValue)
      setCurrentValue(updatedItems);
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = list.findIndex((item) => item.key === active.id);
      const newIndex = list.findIndex((item) => item.key === over.id);
      const newList = arrayMove(list, oldIndex, newIndex);
      setList(newList);
      setFocusKey(newList[newList.length - 1].key);
      updateCurrentValue(newList);
    }
  };
  const commitSortedList = (items = list) => {
    const sorted = orderRef.current.map(
      (itemKey) => find(items, (item) => item.key === itemKey)
    );
    updateCurrentValue(sorted);
  };
  const updateValue = ({ value: value2, key }) => {
    const newList = list.map((item) => {
      if (item.key === key) {
        return {
          ...item,
          value: value2
        };
      }
      return item;
    });
    setList(newList);
    commitSortedList(newList);
  };
  const updateOrderRefs = (updatedList) => {
    orderRef.current = updatedList.map((item) => item.key);
    setFirst(orderRef.current[0]);
    setLast(orderRef.current[orderRef.current.length - 1]);
  };
  const updateListState = (updatedList, focusKey2) => {
    updateCurrentValue(updatedList);
    setList(updatedList);
    setFocusKey(focusKey2);
  };
  const insert = (key) => {
    const temp = [...list];
    const tempKey = v4();
    const emptyItem2 = { value: "", label: "", key: tempKey };
    const index = findIndex(list, (item) => item.key === key);
    temp.splice(index + 1, 0, emptyItem2);
    updateOrderRefs(temp);
    updateListState(temp, tempKey);
  };
  const remove = (key) => {
    if (list.length > 1) {
      const index = findIndex(orderRef.current, (itemKey) => itemKey === key);
      const temp = [...list];
      temp.splice(index, 1);
      const newEmptyItem = { value: "", label: "", key: v4() };
      const filteredList = removeEmptyItems(temp);
      const newList = [...filteredList, newEmptyItem];
      updateOrderRefs(newList);
      updateListState(newList, newEmptyItem.key);
    }
  };
  const focusNext = (key) => {
    var _a2, _b2, _c, _d;
    const currIndex = (_a2 = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _a2.indexOf(key);
    const nextIndex = currIndex < ((_b2 = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _b2.length) - 1 ? currIndex + 1 : ((_c = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _c.length) - 1;
    const nextKey = (_d = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _d[nextIndex];
    setFocusKey(nextKey);
  };
  const focusPrev = (key) => {
    var _a2, _b2;
    const currIndex = (_a2 = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _a2.indexOf(key);
    const prev = currIndex > 0 ? currIndex - 1 : 0;
    const prevKey = (_b2 = orderRef == null ? void 0 : orderRef.current) == null ? void 0 : _b2[prev];
    setFocusKey(prevKey);
  };
  return /* @__PURE__ */ jsx(
    Container,
    {
      error: !!(errors == null ? void 0 : errors.length),
      warning: !!(warnings == null ? void 0 : warnings.length),
      "aria-label": "multivalue editor",
      children: /* @__PURE__ */ jsx(
        DndContext,
        {
          sensors,
          collisionDetection: closestCenter,
          onDragEnd: handleDragEnd,
          children: /* @__PURE__ */ jsx(
            SortableContext,
            {
              items: list.map((item) => item.key),
              strategy: verticalListSortingStrategy,
              children: /* @__PURE__ */ jsx(ListWrapper, { children: list.map((item, idx) => /* @__PURE__ */ jsx(
                ListItem,
                {
                  first: first ? item.key === first : idx === 0,
                  last: last ? item.key === last : idx === list.length - 1,
                  item,
                  updateValue,
                  insert,
                  remove,
                  next: focusNext,
                  prev: focusPrev,
                  focusKey,
                  setFocusKey
                },
                item.key
              )) })
            }
          )
        }
      )
    }
  );
};
named("ListInput", ListInput);
named("MultiValueEditor", Editor);
export {
  Editor
};
